import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  useContext,
} from 'react';

import {
  ftGetBalance,
  ftGetTokenMetadata,
  TokenMetadata,
} from '../services/ft-contract';
import {
  getWhitelistedTokens,
  getWhitelistedTokensInfo,
  getTokenBalances,
  TokenBalancesView,
  getWhitelistedTokensAndNearTokens,
  getGlobalWhitelist,
} from '../services/token';
import {
  toPrecision,
  toReadableNumber,
  toRoundedReadableNumber,
} from '../utils/numbers';
import { toRealSymbol } from '../utils/token';
import { nearMetadata, WRAP_NEAR_CONTRACT_ID } from '../services/wrap-near';
import { Pool } from '../services/pool';
import {
  getBatchTokenNearAccounts,
  getTriTokenIdsOnRef,
  useTriTokenIdsOnRef,
} from '../services/aurora/aurora';
import { getAccountNearBalance } from '../services/near';
import { defaultTokenList, getAuroraConfig } from '../services/aurora/config';
import { getTokenPriceList, getTokens } from '../services/indexer';
import { useWalletSelector } from '../context/WalletSelectorContext';
import { WalletContext, getCurrentWallet } from '../utils/wallets-integration';
import db from '../store/RefDatabase';
import { get_auto_whitelisted_postfix } from '../services/token';

export const usePoolTokens = (pools: Pool[]) => {
  const [poolTokens, setPoolTokens] = useState({});
  useEffect(() => {
    if (!pools.length) return;
    getPoolTokens(pools);
  }, [pools]);
  async function getPoolTokens(pools) {
    const copyPools = JSON.parse(JSON.stringify(pools || []));
    const allRequest = copyPools.map(async (pool) => {
      return await Promise.all(
        pool.tokenIds.map((id) => {
          return ftGetTokenMetadata(id);
        })
      );
    });
    const result = await Promise.all(allRequest);
    const last = result.reduce((pre, tokensOfPool, index) => {
      return {
        ...pre,
        [copyPools[index].id]: tokensOfPool,
      };
    }, {});
    setPoolTokens(last);
  }
  return poolTokens;
};

export const useTokens = (ids: string[] = [], curTokens?: TokenMetadata[]) => {
  const [tokens, setTokens] = useState<TokenMetadata[]>();

  useEffect(() => {
    if (curTokens?.length > 0) {
      setTokens(curTokens);
      return;
    }
    Promise.all<TokenMetadata>(ids.map((id) => ftGetTokenMetadata(id))).then(
      setTokens
    );
  }, [ids.join('')]);

  return tokens;
};

export const useTriTokens = (stopOn?: boolean) => {
  const [triTokens, setTriTokens] = useState<TokenMetadata[]>();
  const auroraTokens = defaultTokenList.tokens;
  const allSupportPairs = getAuroraConfig().Pairs;
  const symbolToAddress = auroraTokens.reduce((pre, token, index) => {
    return {
      ...pre,
      [token.symbol]: token.address,
    };
  }, {});

  const tokenIds = Object.keys(allSupportPairs)
    .map((pairName: string) => {
      const names = pairName.split('-');
      return names.map((symbol) => {
        if (symbol === 'ETH') return getAuroraConfig().WETH;
        else return symbolToAddress[symbol];
      });
    })
    .flat();
  useEffect(() => {
    if (stopOn) return;
    getBatchTokenNearAccounts(tokenIds).then((nearAccounts) => {
      const allIds = nearAccounts.concat(['aurora']);

      return Promise.all(
        allIds.map((addr: string) =>
          ftGetTokenMetadata(addr).then((ftmeta) => ({
            ...ftmeta,
            onTri: true,
          }))
        )
      ).then(setTriTokens);
    });
  }, [stopOn]);
  return !!stopOn ? [] : triTokens?.filter((token) => token.id);
};

export const useRainbowWhitelistTokens = () => {
  const [tokens, setTokens] = useState<TokenMetadata[]>();
  const triTokenIds = useTriTokenIdsOnRef();

  const extraTokenIds = (triTokenIds || []).concat(['aurora']);

  useEffect(() => {
    getWhitelistedTokens()
      .then((tokenIds) => {
        const allTokenIds = [...new Set([...tokenIds, ...extraTokenIds])];
        return Promise.all(
          allTokenIds.map((tokenId) => ftGetTokenMetadata(tokenId))
        );
      })
      .then(setTokens)
      .catch(() => setTokens([]));
  }, [getCurrentWallet()?.wallet?.isSignedIn(), extraTokenIds.join('-')]);

  return tokens?.map((token) => ({ ...token, onRef: true }));
};

export const useWhitelistTokens = (extraTokenIds: string[] = []) => {
  const [tokens, setTokens] = useState<TokenMetadata[]>();
  useEffect(() => {
    getWhitelistedTokensInfo()
      .then(async (tokenInfo) => {
        const { globalWhitelist, userWhitelist } = tokenInfo;
        const allWhiteTokenIds = [
          ...new Set([...globalWhitelist, ...userWhitelist, ...extraTokenIds]),
        ];
        const allTokens = await getAllTokens();
        const postfix = await get_auto_whitelisted_postfix();
        const whiteMetaDataList = await Promise.all(
          allWhiteTokenIds.map((tokenId) => ftGetTokenMetadata(tokenId))
        );
        const globalMetaDataWhitelist = whiteMetaDataList.filter((metadata) =>
          globalWhitelist.includes(metadata.id)
        );
        const whiteMap = whiteMetaDataList.reduce(
          (sum, metadata) => ({ ...sum, ...{ [metadata.id]: metadata } }),
          {}
        );
        allTokens
          .filter((token: TokenMetadata) => {
            return (
              postfix.some((suffix) => token.id.includes(suffix)) &&
              !globalMetaDataWhitelist.find(
                (whitelistedToken) => whitelistedToken.id === token.id
              )
            );
          })
          .map((token) => {
            token.isRisk = true;
            token.isUserToken = !!userWhitelist.includes(token.id);
            whiteMap[token.id] = token;
            return token;
          });
        return Object.values(whiteMap) as TokenMetadata[];
      })
      .then(setTokens)
      .catch(() => setTokens([]));
  }, [getCurrentWallet()?.wallet?.isSignedIn(), extraTokenIds.join('-')]);
  return tokens?.map((token) => ({ ...token, onRef: true }));
};
async function getAllTokens() {
  let allTokens = (await db.queryAllTokens()) || [];
  if (!allTokens.length) {
    const tokens = await getTokens();
    allTokens = Object.keys(tokens).reduce((tokenList, id) => {
      tokenList.push({
        id,
        ...tokens[id],
      });
      return tokenList;
    }, []);
  }
  return allTokens;
}
export const useRiskTokens = () => {
  const tokens = useWhitelistTokens();
  const allRiskTokens = useMemo(() => {
    return tokens?.filter((token) => token.isRisk);
  }, [tokens]);
  return allRiskTokens || [];
};
export const useGlobalWhitelistTokens = (extraTokenIds: string[] = []) => {
  const [tokens, setTokens] = useState<TokenMetadata[]>();
  useEffect(() => {
    getGlobalWhitelist()
      .then((tokenIds) => {
        const allTokenIds = [...new Set([...tokenIds, ...extraTokenIds])];
        return Promise.all(
          allTokenIds.map((tokenId) => ftGetTokenMetadata(tokenId))
        );
      })
      .then(setTokens)
      .catch(() => setTokens([]));
  }, [getCurrentWallet()?.wallet?.isSignedIn(), extraTokenIds.join('-')]);

  return tokens?.map((token) => ({ ...token, onRef: true }));
};

export const useUserRegisteredTokensAllAndNearBalance = () => {
  const [tokens, setTokens] = useState<any[]>();

  useEffect(() => {
    getWhitelistedTokensAndNearTokens()
      .then(async (tokenList) => {
        const triTokenIds = await getTriTokenIdsOnRef();

        const mergedTokenIds = [
          ...new Set((triTokenIds || []).concat(tokenList)),
        ];

        const walletBalancePromise = Promise.all(
          [nearMetadata.id, ...mergedTokenIds].map((tokenId) => {
            return getDepositableBalance(tokenId);
          })
        );
        const tokenMetadataPromise = Promise.all(
          mergedTokenIds.map((tokenId) => ftGetTokenMetadata(tokenId, true))
        );
        return Promise.all([tokenMetadataPromise, walletBalancePromise]);
      })
      .then((result) => {
        const tokenMetadataList = result[0];
        tokenMetadataList.unshift(nearMetadata);
        tokenMetadataList.forEach((token, index) => {
          token.near = result[1][index];
          token.nearNonVisible = result[1][index];
        });
        setTokens(tokenMetadataList);
      });
  }, []);

  return tokens;
};

export const useTokenBalances = () => {
  const [balances, setBalances] = useState<TokenBalancesView>();
  const { accountId } = useWalletSelector();

  const isSignedIn = !!accountId;

  useEffect(() => {
    if (!isSignedIn) return;
    getTokenBalances()
      .then(setBalances)
      .catch(() => setBalances({}));
  }, [isSignedIn, accountId]);

  return balances;
};

export const useWalletTokenBalances = (tokenIds: string[] = []) => {
  const [balances, setBalances] = useState<TokenBalancesView>();

  const near = useDepositableBalance('NEAR');

  useEffect(() => {
    if (tokenIds.some((id) => !id)) return;

    Promise.all<string>(tokenIds.map((id) => ftGetBalance(id))).then(
      (balanceList) => {
        const balances = {};
        balanceList.map((balance, index) => {
          const tokenId: string = tokenIds[index];
          balances[tokenId] = balance;
          if (tokenId === WRAP_NEAR_CONTRACT_ID) {
            balances[tokenId] = near;
          }
        });
        setBalances(balances);
      }
    );
  }, [tokenIds.join(''), near]);

  return balances;
};

export const getDepositableBalance = async (
  tokenId: string,
  decimals?: number
) => {
  if (tokenId === 'NEAR') {
    if (getCurrentWallet()?.wallet?.isSignedIn()) {
      return getAccountNearBalance(
        getCurrentWallet().wallet.getAccountId()
      ).then(({ available }: any) => {
        return toReadableNumber(decimals, available);
      });
    } else {
      return toReadableNumber(decimals, '0');
    }
  } else if (tokenId) {
    if (getCurrentWallet()?.wallet?.isSignedIn()) {
      return ftGetBalance(tokenId)
        .then((balance: string) => {
          return toReadableNumber(decimals, balance);
        })
        .catch((error: any) => '0');
    } else {
      return toReadableNumber(decimals, '0');
    }
  } else {
    return '';
  }
};

export const useTokenPriceList = (dep?: any) => {
  const [tokenPriceList, setTokenPriceList] = useState<Record<string, any>>({});

  const [tokenListTrigger, setTokenListTrigger] = useState<boolean>(false);

  useEffect(() => {
    if (dep) {
      setTokenListTrigger(!tokenListTrigger);
    }
  }, [dep]);

  useEffect(() => {
    getTokenPriceList().then(setTokenPriceList);
  }, [tokenListTrigger]);

  if (Object.keys(tokenPriceList).length > 0) {
    tokenPriceList.NEAR = tokenPriceList?.[WRAP_NEAR_CONTRACT_ID];
  }

  return tokenPriceList;
};

export const useTokensData = (
  tokens: TokenMetadata[],
  balances?: TokenBalancesView,
  visible?: boolean
) => {
  const [count, setCount] = useState(0);
  const [result, setResult] = useState<TokenMetadata[]>([]);
  const fetchIdRef = useRef(0);
  const setResultAtIndex = (tokenWithBalance: TokenMetadata, index: number) => {
    setResult((oldResults) => {
      const newResults = [...oldResults];
      newResults[index] = tokenWithBalance;
      return newResults;
    });
    setCount((previousCount) => previousCount + 1);
  };

  const { accountId } = useWalletSelector();

  const trigger = useCallback(() => {
    setCount(0);
    setResult([]);
    const currentFetchId = fetchIdRef.current;
    for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex++) {
      const index = tokenIndex;
      const token = tokens[index];
      getDepositableBalance(
        token.id === WRAP_NEAR_CONTRACT_ID && token.symbol == 'NEAR'
          ? 'NEAR'
          : token.id,
        token.decimals
      )
        .then((max: string) => {
          if (currentFetchId !== fetchIdRef.current) {
            throw new Error();
          }
          return max;
        })
        .then((max: string) => {
          const nearCount = !!accountId ? toPrecision(max, 3) || '0' : '0';
          const refCount = toRoundedReadableNumber({
            decimals: token.decimals,
            number: balances ? balances[token.id] : '0',
          });
          return {
            ...token,
            asset: toRealSymbol(token.symbol),
            near: Number(nearCount.replace(/[\,]+/g, '')),
            ref: Number(toPrecision(refCount, 3).replace(/[\,]+/g, '')),
            total:
              Number(nearCount.replace(/[\,]+/g, '')) +
              Number(toPrecision(refCount, 3).replace(/[\,]+/g, '')),
          };
        })
        .then((tokenWithBalance: TokenMetadata) =>
          setResultAtIndex(tokenWithBalance, index)
        )
        .catch((error) => {});
    }
  }, [tokens?.length]);

  useEffect(() => {
    if (visible && tokens?.length > 0) {
      trigger();
    }
  }, [tokens?.map((token) => token.id).join('-'), visible]);
  return {
    trigger,
    loading: count < tokens?.length,
    tokensData: result,
  };
};

export const useDepositableBalance = (tokenId: string, decimals?: number) => {
  const [depositable, setDepositable] = useState<string>('');
  const [max, setMax] = useState<string>('');

  const { globalState } = useContext(WalletContext);
  const isSignedIn = globalState.isSignedIn;

  const { wallet } = getCurrentWallet();

  useEffect(() => {
    if (isSignedIn && wallet) {
      if (tokenId === 'NEAR') {
        getAccountNearBalance(getCurrentWallet().wallet.getAccountId()).then(
          ({ available }: any) => setDepositable(available)
        );
      } else if (tokenId) {
        ftGetBalance(tokenId).then(setDepositable);
      }
    } else {
      setDepositable('0');
    }
  }, [tokenId, isSignedIn, wallet]);

  useEffect(() => {
    const max = toReadableNumber(decimals, depositable) || '0';
    setMax(max);
  }, [depositable]);

  return max;
};
